import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  IconButton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Close,
  Person,
  Hotel,
  CalendarToday,
  Payment,
  Email,
  Phone,
  CheckCircle,
  AccessTime,
  Cancel,
  Print,
  Send,
} from '@mui/icons-material';
import { format } from 'date-fns';

export default function ViewBookingDialog({ open, onClose, booking, onEdit }) {
  if (!booking) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Checked In': return 'primary';
      case 'Confirmed': return 'success';
      case 'Checked Out': return 'default';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Checked In': return <CheckCircle />;
      case 'Confirmed': return <CheckCircle />;
      case 'Pending': return <AccessTime />;
      case 'Cancelled': return <Cancel />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Booking Details</Typography>
            <Chip
              label={booking.status}
              size="small"
              color={getStatusColor(booking.status)}
              icon={getStatusIcon(booking.status)}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Booking ID and Status */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Booking ID
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {booking.id}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {booking.createdAt ? format(new Date(booking.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Guest Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Person color="primary" />
                  <Typography variant="h6">Guest Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: '#1976d2', fontSize: '1.5rem' }}>
                    {booking.guest[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {booking.guest}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Primary Guest
                    </Typography>
                  </Box>
                </Box>
                <List dense>
                  <ListItem>
                    <Email fontSize="small" sx={{ mr: 2 }} color="action" />
                    <ListItemText primary={booking.email} secondary="Email" />
                  </ListItem>
                  <ListItem>
                    <Phone fontSize="small" sx={{ mr: 2 }} color="action" />
                    <ListItemText primary={booking.phone || 'N/A'} secondary="Phone" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Room Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Hotel color="primary" />
                  <Typography variant="h6">Room Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="h6" fontWeight="bold">
                          {booking.room}
                        </Typography>
                      }
                      secondary="Room"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={`${booking.numberOfGuests || 1} Guest${booking.numberOfGuests > 1 ? 's' : ''}`}
                      secondary="Number of Guests"
                    />
                  </ListItem>
                  {booking.specialRequests && (
                    <ListItem>
                      <ListItemText
                        primary={booking.specialRequests}
                        secondary="Special Requests"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Stay Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CalendarToday color="primary" />
                  <Typography variant="h6">Stay Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="bold">
                          {booking.checkIn}
                        </Typography>
                      }
                      secondary="Check-in Date"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="bold">
                          {booking.checkOut}
                        </Typography>
                      }
                      secondary="Check-out Date"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="bold">
                          {booking.nights} Night{booking.nights > 1 ? 's' : ''}
                        </Typography>
                      }
                      secondary="Duration"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Payment color="primary" />
                  <Typography variant="h6">Payment Information</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          {booking.amount}
                        </Typography>
                      }
                      secondary="Total Amount"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={booking.paymentMethod ? booking.paymentMethod.replace('_', ' ').toUpperCase() : 'N/A'}
                      secondary="Payment Method"
                    />
                  </ListItem>
                  {booking.depositAmount && (
                    <ListItem>
                      <ListItemText
                        primary={`$${booking.depositAmount.toFixed(2)}`}
                        secondary="Deposit Paid"
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Chip
                          label={booking.paymentStatus || 'Pending'}
                          size="small"
                          color={booking.paymentStatus === 'paid' ? 'success' : 'warning'}
                        />
                      }
                      secondary="Payment Status"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Notes */}
          {booking.specialRequests && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Special Requests
                  </Typography>
                  <Typography variant="body1">
                    {booking.specialRequests}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button startIcon={<Print />}>Print</Button>
        <Button startIcon={<Send />}>Send Email</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={() => {
            onClose();
            onEdit(booking);
          }}
        >
          Edit Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
}
